import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form as FormikForm } from "formik";
import { Button, Row, Col } from "react-bootstrap";
import {
  FaUser,
  FaBuilding,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaGlobe,
  FaShoppingCart,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { ordersApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import FormField from "../components/forms/FormField";
import { checkoutSchema } from "../validation/schemas";
import { UAE_COUNTRY, UAE_EMIRATES } from "../constants/uae";
import "./Checkout.css";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../constants/seo";
import { FormPageSkeleton } from "../components/Skeleton";

const Checkout = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { cart, clearCart, loading: cartLoading } = useCart();

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/checkout" }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!authLoading && isAuthenticated && !cartLoading && !cart.items?.length) {
      navigate("/cart", { replace: true });
    }
  }, [authLoading, isAuthenticated, cartLoading, cart.items, navigate]);

  const initialValues = {
    fullName: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    companyName: "",
    line1: "",
    line2: "",
    city: "",
    emirate: "",
    country: UAE_COUNTRY,
    notes: "",
  };

  if (authLoading || cartLoading || !isAuthenticated) {
    return (
      <main className="checkout-page">
        <Seo {...PAGE_SEO.checkout} />
        <section className="checkout-section">
          <FormPageSkeleton />
        </section>
      </main>
    );
  }

  const cartItems = cart.items || [];

  return (
    <main className="checkout-page">
      <Seo {...PAGE_SEO.checkout} />
      <section className="checkout-hero">
        <span className="checkout-badge">
          <FaShoppingCart /> Checkout
        </span>
        <h1>Complete Your Order</h1>
        <p>
          Enter your delivery details below. Name, email, and phone are taken from
          your account and can be updated if needed.
        </p>
      </section>

      <section className="checkout-section">
        <div className="checkout-layout">
          <div className="checkout-form-wrap">
            <h2>Delivery Details</h2>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={checkoutSchema}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  const res = await ordersApi.checkout({
                    shippingAddress: {
                      fullName: values.fullName,
                      email: values.email,
                      phone: values.phone,
                      companyName: values.companyName,
                      line1: values.line1,
                      line2: values.line2,
                      city: values.city,
                      emirate: values.emirate,
                      country: UAE_COUNTRY,
                    },
                    notes: values.notes,
                  });
                  toast.success(`Order placed: ${res.data.data.order.orderNumber}`);
                  clearCart();
                  navigate("/orders", { replace: true });
                } catch (error) {
                  toast.error(getErrorMessage(error, "Checkout failed"));
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ isSubmitting }) => (
                <FormikForm className="checkout-form">
                  <Row>
                    <Col md={6}>
                      <FormField
                        name="fullName"
                        label="Full Name"
                        labelIcon={<FaUser />}
                        placeholder="Your full name"
                      />
                    </Col>
                    <Col md={6}>
                      <FormField
                        name="email"
                        type="email"
                        label="Email"
                        labelIcon={<FaEnvelope />}
                        placeholder="you@example.com"
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormField
                        name="phone"
                        label="Phone"
                        labelIcon={<FaPhoneAlt />}
                        placeholder="+971 50 000 0000"
                      />
                    </Col>
                    <Col md={6}>
                      <FormField
                        name="companyName"
                        label="Company Name"
                        labelIcon={<FaBuilding />}
                        placeholder="Optional"
                      />
                    </Col>
                  </Row>

                  <FormField
                    name="line1"
                    label="Delivery Address"
                    labelIcon={<FaMapMarkerAlt />}
                    placeholder="Building, street, area"
                  />

                  <FormField
                    name="line2"
                    label="Address Line 2"
                    placeholder="Apartment, floor, landmark (optional)"
                  />

                  <Row>
                    <Col md={6}>
                      <FormField name="city" label="City" placeholder="City" />
                    </Col>
                    <Col md={6}>
                      <FormField name="emirate" label="Emirate" as="select">
                        <option value="">Select emirate</option>
                        {UAE_EMIRATES.map((emirate) => (
                          <option key={emirate} value={emirate}>
                            {emirate}
                          </option>
                        ))}
                      </FormField>
                    </Col>
                  </Row>

                  <FormField
                    name="country"
                    label="Country"
                    labelIcon={<FaGlobe />}
                    readOnly
                    disabled
                  />

                  <FormField
                    name="notes"
                    type="textarea"
                    rows={3}
                    label="Order Notes"
                    placeholder="Any special instructions (optional)"
                  />

                  <div className="checkout-actions">
                    <Link to="/cart" className="checkout-back-link">
                      Back to Cart
                    </Link>
                    <Button type="submit" className="checkout-submit-btn" disabled={isSubmitting}>
                      {isSubmitting ? "Placing Order..." : "Place Order"}
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>

          <aside className="checkout-summary">
            <h2>Order Summary</h2>
            <ul className="checkout-items">
              {cartItems.map((item) => (
                <li key={item.id}>
                  <span>
                    {item.name} × {item.quantity}
                  </span>
                  <strong>AED {(Number(item.price) * item.quantity).toFixed(2)}</strong>
                </li>
              ))}
            </ul>

            <div className="summary-row">
              <span>Subtotal</span>
              <strong>AED {Number(cart.subtotal || 0).toFixed(2)}</strong>
            </div>
            <div className="summary-row">
              <span>VAT 5%</span>
              <strong>AED {Number(cart.vatAmount || 0).toFixed(2)}</strong>
            </div>
            <div className="summary-row">
              <span>Delivery</span>
              <strong>To be confirmed</strong>
            </div>
            <div className="summary-total">
              <span>Total</span>
              <strong>AED {Number(cart.total || 0).toFixed(2)}</strong>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default Checkout;
